import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContextSupabase';
import Layout from './components/Layout.tsx';
import Landing from './pages/Landing.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Login from './pages/Login.tsx';
import UserApproval from './pages/UserApproval.tsx';
import Collaborations from './pages/Collaborations.tsx';
import CollaborationDetails from './pages/CollaborationDetails.tsx';
import CollaborationForm from './pages/CollaborationForm.tsx';
import Clients from './pages/Clients.tsx';
import ClientDetails from './pages/ClientDetails.tsx';
import Influencers from './pages/Influencers.tsx';
import Resources from './pages/Resources.tsx';
import Account from './pages/Account.tsx';
import SuperAdmin from './pages/SuperAdmin.tsx';
import AgencyDetails from './pages/AgencyDetails.tsx';
import Accounting from './pages/Accounting.tsx';
import Casting from './pages/Casting.tsx';
import Assistant from './pages/Assistant.tsx';
import Todo from './pages/Todo.tsx';
import Recruitment from './pages/Recruitment.tsx';
import DailyBrief from './pages/DailyBrief.tsx';
import InfluencerBrief from './pages/InfluencerBrief.tsx';
import PublicInfluencerView from './pages/PublicInfluencerView.tsx';
import PublicCampaignView from './pages/PublicCampaignView.tsx';
import PublicCastingView from './pages/PublicCastingView.tsx';
import PublicCollaborationView from './pages/PublicCollaborationView.tsx';
import RestrictedLayout from './components/RestrictedLayout.tsx';
import ApifyIntegration from './pages/ApifyIntegration.tsx';
import Discover from './pages/Discover.tsx';
import Pricing from './pages/Pricing.tsx';
import MarketWatch from './pages/MarketWatch.tsx';
import MentionsLegales from './pages/MentionsLegales.tsx';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite.tsx';
import CGV from './pages/CGV.tsx';
import TalentManager from './pages/TalentManager.tsx';
import OAuthCallback from './pages/OAuthCallback.tsx';
import Scoot from './pages/Scoot.tsx';
import MatchingIA from './pages/MatchingIA.tsx';
import Credits from './pages/Credits.tsx';
import Prospection from './pages/Prospection.tsx';
import Pepites from './pages/Pepites.tsx';
import PepitesConfig from './pages/PepitesConfig.tsx';
import CastingInterne from './pages/CastingInterne.tsx';
import PublicRosterView from './pages/PublicRosterView.tsx';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, userProfile, loading } = useAuth();

  console.log('PrivateRoute Debug:', {
    loading,
    currentUser: currentUser?.email,
    userProfile
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    console.log('No currentUser, redirecting to login');
    return <Navigate to="/login" />;
  }

  if (currentUser.email?.endsWith('@swipe-upp.com')) {
    console.log('Swipe-upp team member detected, allowing access');
    return <>{children}</>;
  }

  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }
  
  if (!currentUser || currentUser.email !== 'hosni@swipe-upp.com') {
    return <Navigate to="/app" />;
  }

  return <>{children}</>;
}

function SuperAdminOnlyRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  if (!currentUser || currentUser.email !== 'hosni@swipe-upp.com') {
    return <Navigate to="/app" />;
  }

  return <>{children}</>;
}

function SwipeUppOnlyRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!currentUser || !currentUser.email?.endsWith('@swipe-upp.com')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600 mb-6">
            Cette page est réservée à l'équipe Swipe-Upp.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function PermissionRoute({ children, permission }: { children: React.ReactNode; permission: string }) {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // Les pages de découverte (discover, recruitment) sont accessibles à tous les utilisateurs avec abonnement
  const publicPermissions = ['discover', 'recruitment'];
  if (publicPermissions.includes(permission)) {
    return <>{children}</>;
  }

  if (currentUser.email === 'hosni@swipe-upp.com' || userProfile?.user_role === 'admin') {
    return <>{children}</>;
  }

  const userPermissions = userProfile?.permissions || {};
  if (!userPermissions[permission]) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600 mb-6">
            Vous n'avez pas la permission d'accéder à cette page. Contactez votre administrateur pour obtenir l'accès.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
        <Route path="/cgv" element={<CGV />} />
        {/* Public influencer brief routes */}
        <Route path="/brief/:agencyName/:influencerSlug" element={<InfluencerBrief />} />
        <Route path="/brief/:influencerSlug" element={<InfluencerBrief />} />
        {/* Public influencer view route */}
        <Route path="/influencer/:influencerId" element={<PublicInfluencerView />} />
        {/* Public campaign tracking route */}
        <Route path="/suivi/:token" element={<PublicCampaignView />} />
        {/* Public casting validation route */}
        <Route path="/casting/:token" element={<PublicCastingView />} />
        {/* Public collaboration details route */}
        <Route path="/collaboration/:id" element={<PublicCollaborationView />} />
        {/* Public roster view route */}
        <Route path="/r/:slug" element={<PublicRosterView />} />
        {/* OAuth callback route */}
        <Route path="/api/oauth/google/callback" element={<OAuthCallback />} />
        <Route
          path="/superadmin"
          element={
            <SuperAdminRoute>
              <SuperAdmin />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/superadmin/user-approval"
          element={
            <SuperAdminRoute>
              <UserApproval />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/superadmin/agency/:agencyId"
          element={
            <SuperAdminRoute>
              <AgencyDetails />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/app"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/app/discover" replace />} />
          <Route path="dashboard" element={
            <PermissionRoute permission="dashboard">
              <Dashboard />
            </PermissionRoute>
          } />
          <Route path="collaborations" element={
            <PermissionRoute permission="collaborations">
              <Collaborations />
            </PermissionRoute>
          } />
          <Route path="collaborations/new" element={
            <PermissionRoute permission="collaborations">
              <CollaborationForm />
            </PermissionRoute>
          } />
          <Route path="collaborations/:id" element={
            <PermissionRoute permission="collaborations">
              <CollaborationDetails />
            </PermissionRoute>
          } />
          <Route path="casting" element={
            <PermissionRoute permission="casting">
              <Casting />
            </PermissionRoute>
          } />
          <Route path="casting-interne" element={
            <PermissionRoute permission="casting_interne">
              <CastingInterne />
            </PermissionRoute>
          } />
          <Route path="recruitment" element={
            <PermissionRoute permission="recruitment">
              <Recruitment />
            </PermissionRoute>
          } />
          <Route path="daily-brief" element={
            <PermissionRoute permission="daily_brief">
              <DailyBrief />
            </PermissionRoute>
          } />
          <Route path="clients" element={
            <PermissionRoute permission="clients">
              <Clients />
            </PermissionRoute>
          } />
          <Route path="clients/:id" element={
            <PermissionRoute permission="clients">
              <ClientDetails />
            </PermissionRoute>
          } />
          <Route path="influencers" element={
            <PermissionRoute permission="influencers">
              <Influencers />
            </PermissionRoute>
          } />
          <Route path="discover" element={
            <PermissionRoute permission="discover">
              <Discover />
            </PermissionRoute>
          } />
          <Route path="matching-ia" element={
            <PermissionRoute permission="matching_ia">
              <MatchingIA />
            </PermissionRoute>
          } />
          <Route path="market-watch" element={
            <PermissionRoute permission="discover">
              <MarketWatch />
            </PermissionRoute>
          } />
          <Route path="pepites" element={
            <SwipeUppOnlyRoute>
              <Pepites />
            </SwipeUppOnlyRoute>
          } />
          <Route path="pepites/config" element={
            <SwipeUppOnlyRoute>
              <PepitesConfig />
            </SwipeUppOnlyRoute>
          } />
          <Route path="scoot" element={
            <SwipeUppOnlyRoute>
              <Scoot />
            </SwipeUppOnlyRoute>
          } />
          <Route path="apify-integration" element={
            <SuperAdminOnlyRoute>
              <ApifyIntegration />
            </SuperAdminOnlyRoute>
          } />
          <Route path="resources" element={
            <PermissionRoute permission="resources">
              <Resources />
            </PermissionRoute>
          } />
          <Route path="user-approval" element={<UserApproval />} />
          <Route path="account" element={<Account />} />
          <Route path="credits" element={<Credits />} />
          <Route path="prospection" element={
            <PermissionRoute permission="prospection">
              <Prospection />
            </PermissionRoute>
          } />
          <Route path="accounting" element={
            <PermissionRoute permission="accounting">
              <Accounting />
            </PermissionRoute>
          } />
          <Route path="todo" element={
            <PermissionRoute permission="todo">
              <Todo />
            </PermissionRoute>
          } />
          <Route
            path="assistant"
            element={
              <SuperAdminOnlyRoute>
                <Assistant />
              </SuperAdminOnlyRoute>
            }
          />
          <Route path="talent-manager" element={
            <SwipeUppOnlyRoute>
              <TalentManager />
            </SwipeUppOnlyRoute>
          } />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;